import { Heading, PageHeader } from '@ksyos/design-system';
import { useNavigate, useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { OrganizationDataError } from '@common/interfaces/api/organizationDataErrorsApi';
import type { IOrganization } from '@frontend/components/interfaces';
import { ManagerAuthorization } from '@common/enums/index';
import { AddOrDeactivatePractitionerButton } from '@frontend/components/elements/AddOrDeactivatePractitionerButton';
import { getSelectedLanguage } from '@frontend/translate';
import { hasAccountRequestAuthorization, hasOneAuthorization, useAbortableFetch } from '@frontend/utils';
import { ButtonHeader } from '../../elements/ButtonHeader';
import { AccountDeactivationModal } from '../accountDeactivation/AccountDeactivationModal';
import { AccountRequestAdd } from '../accountRequests/AccountRequestAdd';
import { OrganizationCarePathsOverview } from './OrganizationCarePathsOverview';
import { OrganizationCareProviderDetails } from './OrganizationCareProviderDetails';
import { OrganizationPractitionerAdd } from './OrganizationPractitionerAdd';
import { OrganizationStatusAlert } from './OrganizationStatusAlert';

export function OrganizationOverview() {
    const navigate = useNavigate();
    const translate = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();
    const { organizationId } = useParams() as { organizationId: string };

    const [isAddNewPractitionerModalOpen, setIsAddNewPractitionerModalOpen] = useState(false);
    const [isDeactivateEmploymentModalOpen, setIsDeactivateEmploymentModalOpen] = useState(false);
    const [organization, setOrganization] = useState<IOrganization>();
    const [organizationDataErrors, setOrganizationDataErrors] = useState<OrganizationDataError[]>([]);

    const hasPractitionerAddEditAuthorization = hasOneAuthorization(ManagerAuthorization.practitionerAddEdit);
    const hasAccountRequestCreateAuthorization = hasAccountRequestAuthorization('create');
    const hasOrganizationAddEditAuthorization = hasOneAuthorization(ManagerAuthorization.organizationAddEdit);

    useEffect(() => {
        loadOrganization(organizationId);
        loadOrganizationDataErrors(organizationId);
    }, [organizationId]);

    function handleCloseModal(itemCreated?: boolean) {
        setIsAddNewPractitionerModalOpen(false);

        if (itemCreated) {
            navigate(0);
        }
    }

    function handleCloseAccountDeactivationModals(accountDeactivated?: boolean) {
        setIsDeactivateEmploymentModalOpen(false);

        if (accountDeactivated) {
            navigate(0);
        }
    }

    async function loadOrganization(id: string) {
        const url = `/api/organizations/${id}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setOrganization(result.json);
    }

    async function loadOrganizationDataErrors(id: string) {
        if (!hasOrganizationAddEditAuthorization) {
            return;
        }
        const url = `/api/organizations/${id}/dataErrors`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setOrganizationDataErrors(result.json);
    }

    return (
        <>
            <PageHeader title={translate.organization.organizationOverview} />
            {hasOrganizationAddEditAuthorization && organization && (
                <OrganizationStatusAlert
                    organization={organization}
                    organizationDataErrors={organizationDataErrors}
                />
            )}
            <OrganizationCarePathsOverview
                headingSize="large"
                showCareProviders={false}
            />

            {(hasPractitionerAddEditAuthorization || hasAccountRequestCreateAuthorization) && (
                <ButtonHeader title={<Heading size="large">{translate.organization.practitioners}</Heading>}>
                    <AddOrDeactivatePractitionerButton
                        onClickAddPractitioner={() => setIsAddNewPractitionerModalOpen(true)}
                        onClickDeactivatePractitioner={() => setIsDeactivateEmploymentModalOpen(true)}
                        organizationId={organizationId}
                    />
                </ButtonHeader>
            )}

            <OrganizationCareProviderDetails
                avatarSize={48}
                displayPractitionerStatusBadge
                isKsyos={false}
                organizationId={organizationId}
                padding="medium"
                shadow
            />

            {isAddNewPractitionerModalOpen &&
                (hasPractitionerAddEditAuthorization ? (
                    <OrganizationPractitionerAdd
                        isOpen={isAddNewPractitionerModalOpen}
                        onClose={handleCloseModal}
                        organizationId={organizationId}
                    />
                ) : (
                    <AccountRequestAdd
                        isOpen={isAddNewPractitionerModalOpen}
                        onClose={handleCloseModal}
                        organizationId={organizationId}
                    />
                ))}

            {isDeactivateEmploymentModalOpen && (
                <AccountDeactivationModal
                    isOpen={isDeactivateEmploymentModalOpen}
                    onClose={handleCloseAccountDeactivationModals}
                    organizationId={organizationId}
                />
            )}
        </>
    );
}
